<template>
  <div class="admin-dashboard">
    <div v-if="!embedded" class="dashboard-page-actions">
      <span class="dashboard-page-actions__meta">{{ $t('common.lastUpdated') }}: {{ formatRefreshTime(lastUpdatedAt) }}</span>
      <CButton color="primary" class="font-weight-bold" size="sm" :disabled="dashboardLoading" @click="refreshDashboard">
        {{ $t('common.refresh') }}
      </CButton>
    </div>

    <CCard v-if="!embedded" class="dashboard-hero border-0 shadow-sm mb-4">
      <CCardBody class="p-4 p-lg-5">
        <CRow class="align-items-center">
          <CCol lg="8">
            <div class="hero-kicker">{{ $t('pages.dashboard.admin.heroKicker') }}</div>
            <h2 class="hero-title">{{ $t('pages.dashboard.admin.heroTitle') }}</h2>
            <p class="hero-copy">
              {{ $t('pages.dashboard.admin.heroCopy') }}
            </p>
            <div class="hero-actions">
              <CButton color="primary" class="font-weight-bold mr-2 mb-2" to="/agreements">
                {{ $t('pages.dashboard.admin.viewAgreements') }}
              </CButton>
              <CButton color="secondary" variant="outline" class="mr-2 mb-2" to="/review-queue">
                {{ $t('pages.dashboard.admin.reviewQueue') }}
              </CButton>
              <CButton color="secondary" variant="outline" class="mb-2" @click="exportDashboard">
                {{ $t('pages.dashboard.admin.openReports') }}
              </CButton>
            </div>
          </CCol>
          <CCol lg="4">
            <CCard class="hero-side-card border-0 shadow-sm mt-4 mt-lg-0">
              <CCardBody>
                <div class="hero-side-label">{{ text.pendingReviews }}</div>
                <div class="hero-side-value">{{ displayPendingReviews }}</div>
                <div class="hero-side-copy">{{ text.pendingReviewsCopy }}</div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CRow class="mb-2">
      <CCol md="6" xl="3" v-for="item in summaryStats" :key="item.label" class="mb-4">
        <CCard class="stat-card border-0 shadow-sm h-100" @click.native="goTo(item.to)">
          <CCardBody>
            <div class="stat-header">
              <div class="stat-label">{{ item.label }}</div>
              <div class="stat-icon" :class="item.iconClass">{{ item.icon }}</div>
            </div>
            <div class="stat-value">{{ item.value }}</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol lg="8" class="mb-4">
        <CCard class="panel-card panel-card--chart border-0 shadow-sm">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ text.activityOverview }}</div>
              <div class="panel-subtitle">{{ text.activityOverviewCopy }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="chart-card-body pt-0">
            <CChartBar
              :datasets="workflowChart.datasets"
              :labels="workflowChart.labels"
              :options="workflowChartOptions"
            />
            <div class="chart-insights">
              <div class="chart-insights__headline">{{ chartInsightHeadline }}</div>
              <div class="chart-insights__grid">
                <div class="chart-insights__item">
                  <span class="chart-insights__label">{{ text.busiestMonth }}</span>
                  <strong class="chart-insights__value">{{ chartBusiestMonth }}</strong>
                </div>
                <div class="chart-insights__item">
                  <span class="chart-insights__label">{{ text.mostActiveStatus }}</span>
                  <strong class="chart-insights__value">{{ chartTopStatus }}</strong>
                </div>
                <div class="chart-insights__item">
                  <span class="chart-insights__label">{{ text.currentFocus }}</span>
                  <strong class="chart-insights__value">{{ chartFocusMessage }}</strong>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ text.quickActions }}</div>
              <div class="panel-subtitle">{{ text.quickActionsCopy }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <button v-for="item in quickActions" :key="item.label" class="quick-action" @click="goTo(item.to)">
              <span class="quick-action__icon">{{ item.icon }}</span>
              <span class="quick-action__content">
                <span class="quick-action__label">{{ item.label }}</span>
                <span class="quick-action__copy">{{ item.copy }}</span>
              </span>
              <span class="quick-action__badge">{{ item.badge }}</span>
            </button>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol lg="12" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ text.reviewQueueTitle }}</div>
              <div class="panel-subtitle">{{ text.reviewQueueCopy }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <div v-for="item in queueItems" :key="item.id" class="queue-item">
              <div class="queue-item__content">
                <div class="queue-item__title">{{ item.title }}</div>
                <div class="queue-item__meta">{{ item.meta }}</div>
              </div>
                <CButton size="sm" color="secondary" variant="outline" @click="openReview(item.id)">
                {{ text.open }}
              </CButton>
            </div>
            <CButton color="link" class="px-0 mt-2" to="/review-queue">
              {{ text.viewFullQueue }}
            </CButton>
          </CCardBody>
        </CCard>
      </CCol>

    </CRow>

    <CRow>
      <CCol md="6" xl="4" class="mb-4" v-for="card in insightCards" :key="card.title">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0 pb-2">
            <div>
              <div class="panel-title panel-title--small">{{ card.title }}</div>
              <div class="panel-subtitle">{{ card.subtitle }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <div class="data-list">
              <div v-for="item in card.items" :key="item.label" class="data-row">
                <span class="data-row__label">{{ item.label }}</span>
                <span class="data-row__value">{{ item.value }}</span>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { CChartBar } from '@coreui/vue-chartjs'
import Service from '@/service/api'
import { readPageCache, writePageCache } from '@/utils/page-cache'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'

const PAGE_TEXT = {
  en: {
    pendingReviews: 'Pending Reviews',
    pendingReviewsCopy: 'Documents still waiting for admin routing or final decision.',
    activityOverview: 'Admin Activity Overview',
    activityOverviewCopy: 'Agreement volume by workflow status across recent months',
    busiestMonth: 'Busiest Month',
    mostActiveStatus: 'Most Active Status',
    currentFocus: 'Current Focus',
    quickActions: 'Admin Quick Actions',
    quickActionsCopy: 'Jump straight to common agreement management tasks',
    reviewQueueTitle: 'Pending Review Queue',
    reviewQueueCopy: 'Documents that still need admin attention',
    open: 'Open',
    viewFullQueue: 'View Full Queue',
    totalAgreements: 'Total Agreements',
    draftAgreements: 'Draft Agreements',
    trustedDevices: 'Trusted Devices',
    draft: 'Draft',
    pending: 'Pending',
    approved: 'Approved',
    noActivityYet: 'No activity yet',
    noDominantStatus: 'No dominant status',
    focusClearPending: 'Clear pending review queue',
    focusConvertDrafts: 'Convert drafts into review-ready records',
    focusSustainMomentum: 'Sustain approval momentum',
    focusBuildActivity: 'Build agreement activity',
    noAgreementActivity: 'No agreement activity has been recorded in this dashboard yet.',
    recentWorkflowActivity: (status) => `Recent workflow activity is centered around ${status.toLowerCase()}.`,
    noAgreementData: 'No agreement data',
    accountsWithTrustedDevices: 'Accounts with trusted devices',
    accountsWithoutTrustedDevices: 'Accounts without trusted devices',
    savedTrustedDevices: 'Saved trusted devices',
    averageDevicesPerAccount: 'Average devices per account',
    agreementTypes: 'Agreement Types',
    agreementTypesCopy: 'Current MOU mix in the dashboard dataset',
    stageDistribution: 'Agreement Stage Distribution',
    stageDistributionCopy: 'Where agreements currently sit in the workflow',
    reviewSnapshot: 'Agreement Review Snapshot',
    reviewSnapshotCopy: 'Review and approval progress at a glance',
    noReviewData: 'No review data',
    pendingReviewsStat: 'Pending reviews',
    approvedAgreements: 'Approved agreements',
    draftAgreementsStat: 'Draft agreements',
    completionRate: 'Completion rate',
    agreementTypeFallback: 'Agreement',
    quickActionViewAgreements: 'View Agreements',
    quickActionViewAgreementsCopy: 'Browse all MOU records and statuses',
    quickActionOpenQueue: 'Open Review Queue',
    quickActionOpenQueueCopy: 'Continue pending agreement review work',
    quickActionTranslation: 'Translation Requests',
    quickActionTranslationCopy: 'Follow agreement translation activity',
    justNow: 'Just now'
  },
  th: {
    pendingReviews: 'รายการที่รอรีวิว',
    pendingReviewsCopy: 'เอกสารที่ยังรอการส่งต่อหรือการตัดสินใจขั้นสุดท้ายจากผู้ดูแลระบบ',
    activityOverview: 'ภาพรวมกิจกรรมของผู้ดูแลระบบ',
    activityOverviewCopy: 'จำนวนข้อตกลงแยกตามสถานะเวิร์กโฟลว์ในช่วงหลายเดือนที่ผ่านมา',
    busiestMonth: 'เดือนที่หนาแน่นที่สุด',
    mostActiveStatus: 'สถานะที่เคลื่อนไหวมากที่สุด',
    currentFocus: 'จุดโฟกัสปัจจุบัน',
    quickActions: 'การดำเนินการด่วนของผู้ดูแลระบบ',
    quickActionsCopy: 'ไปยังงานจัดการข้อตกลงที่ใช้บ่อยได้ทันที',
    reviewQueueTitle: 'คิวรีวิวที่รอดำเนินการ',
    reviewQueueCopy: 'เอกสารที่ยังต้องการการดำเนินการจากผู้ดูแลระบบ',
    open: 'เปิด',
    viewFullQueue: 'ดูคิวทั้งหมด',
    totalAgreements: 'ข้อตกลงทั้งหมด',
    draftAgreements: 'ข้อตกลงฉบับร่าง',
    trustedDevices: 'อุปกรณ์ที่เชื่อถือได้',
    draft: 'ฉบับร่าง',
    pending: 'รอดำเนินการ',
    approved: 'อนุมัติแล้ว',
    noActivityYet: 'ยังไม่มีกิจกรรม',
    noDominantStatus: 'ยังไม่มีสถานะเด่นชัด',
    focusClearPending: 'เคลียร์คิวรีวิวที่ค้างอยู่',
    focusConvertDrafts: 'เปลี่ยนฉบับร่างให้พร้อมเข้าสู่การรีวิว',
    focusSustainMomentum: 'รักษาความต่อเนื่องของการอนุมัติ',
    focusBuildActivity: 'เพิ่มกิจกรรมของข้อตกลง',
    noAgreementActivity: 'ยังไม่มีการบันทึกกิจกรรมของข้อตกลงในแดชบอร์ดนี้',
    recentWorkflowActivity: (status) => `กิจกรรมเวิร์กโฟลว์ล่าสุดเน้นอยู่ที่ ${status.toLowerCase()}`,
    noAgreementData: 'ไม่มีข้อมูลข้อตกลง',
    accountsWithTrustedDevices: 'บัญชีที่มีอุปกรณ์ที่เชื่อถือได้',
    accountsWithoutTrustedDevices: 'บัญชีที่ไม่มีอุปกรณ์ที่เชื่อถือได้',
    savedTrustedDevices: 'อุปกรณ์ที่เชื่อถือได้ที่บันทึกไว้',
    averageDevicesPerAccount: 'จำนวนอุปกรณ์เฉลี่ยต่อบัญชี',
    agreementTypes: 'ประเภทข้อตกลง',
    agreementTypesCopy: 'ภาพรวมประเภท MOU ในชุดข้อมูลแดชบอร์ด',
    stageDistribution: 'การกระจายตามขั้นของข้อตกลง',
    stageDistributionCopy: 'ตำแหน่งที่ข้อตกลงอยู่ในเวิร์กโฟลว์ปัจจุบัน',
    reviewSnapshot: 'ภาพรวมการรีวิวข้อตกลง',
    reviewSnapshotCopy: 'ความคืบหน้าของการรีวิวและการอนุมัติโดยสรุป',
    noReviewData: 'ไม่มีข้อมูลรีวิว',
    pendingReviewsStat: 'รายการที่รอรีวิว',
    approvedAgreements: 'ข้อตกลงที่อนุมัติแล้ว',
    draftAgreementsStat: 'ข้อตกลงฉบับร่าง',
    completionRate: 'อัตราความสำเร็จ',
    agreementTypeFallback: 'ข้อตกลง',
    quickActionViewAgreements: 'ดูข้อตกลง',
    quickActionViewAgreementsCopy: 'เรียกดูรายการ MOU และสถานะทั้งหมด',
    quickActionOpenQueue: 'เปิดคิวรีวิว',
    quickActionOpenQueueCopy: 'ทำงานรีวิวที่ค้างอยู่ต่อ',
    quickActionTranslation: 'คำขอแปล',
    quickActionTranslationCopy: 'ติดตามกิจกรรมการแปลของข้อตกลง',
    justNow: 'เมื่อสักครู่'
  }
}

const ADMIN_DASHBOARD_CACHE_KEY = 'admin-dashboard-page'
const ADMIN_DASHBOARD_CACHE_MAX_AGE = 5 * 60 * 1000

function isFreshTimestamp(value, maxAgeMs) {
  if (!value) return false
  const timestamp = new Date(value).getTime()
  if (!timestamp) return false
  return (Date.now() - timestamp) < maxAgeMs
}

function getStageLabel(item) {
  if (!item) return ''
  if (item.status === 'approved') return 'Approved'
  if (item.status === 'revision' || item.status === 'rejected') return 'Returned for User Revision'
  if (isReadyForAdminFinalApproval(item)) return 'Waiting for approval'
  return {
    1: 'Draft Submitted',
    2: 'Assign Committee',
    3: 'Committee Review and Comments'
  }[Math.min(Number(item.currentStep || 1), 3)] || 'Draft Submitted'
}

function pickLangValue(items) {
  if (!Array.isArray(items)) return ''
  const found = items.find(item => item && item.value)
  return found ? String(found.value) : ''
}

function getAccountName(item) {
  const firstName = item && item.userinfo && Array.isArray(item.userinfo.firstName) ? pickLangValue(item.userinfo.firstName) : ''
  const lastName = item && item.userinfo && Array.isArray(item.userinfo.lastName) ? pickLangValue(item.userinfo.lastName) : ''
  return [firstName, lastName].filter(Boolean).join(' ').trim() || String(item && (item.email || item.code || 'Unknown'))
}

function getGroupLabel(group) {
  if (!group || typeof group !== 'object') return 'Unknown'
  return pickLangValue(group.title) || String(group.key || group.name || 'Unknown')
}

function formatMonthLabel(value) {
  const date = new Date(value.getFullYear(), value.getMonth(), 1)
  return date.toLocaleString('en-US', { month: 'short' })
}

function toDate(value) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export default {
  name: 'DashboardAdmin',
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CChartBar
  },
  data() {
    return {
      accounts: [],
      assignments: [],
      workflows: [],
      lastUpdatedAt: '',
      dashboardLoading: false,
      accountsLoaded: false,
      assignmentsLoaded: false,
      workflowsLoaded: false,
      workflowChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          position: 'bottom'
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0
            }
          }]
        }
      }
    }
  },
  computed: {
    localeCode() {
      return this.$i18n && this.$i18n.locale === 'en' ? 'en' : 'th'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    activeAccounts() {
      return this.accounts.filter(item => item.statusKey === 'ACTIVE').length
    },
    assignmentCount() {
      return this.assignments.length
    },
    activeAssignmentCount() {
      return this.assignments.filter(item => item.active).length
    },
    groupCount() {
      return new Set(this.assignments.map(item => item.groupLabel).filter(Boolean)).size
    },
    trustedDevices() {
      return this.accounts.reduce((total, item) => total + item.trustedDevices, 0)
    },
    managedAccounts() {
      return this.accounts.length
    },
    pendingReviews() {
      return this.workflows.filter(item => item.status !== 'approved').length
    },
    approvedReviews() {
      return this.workflows.filter(item => item.status === 'approved').length
    },
    draftAgreements() {
      return this.workflows.filter(item => item.status === 'draft').length
    },
    totalAgreements() {
      return this.workflows.length
    },
    accountsWithTrustedDevices() {
      return this.accounts.filter(item => item.trustedDevices > 0).length
    },
    assignmentCoverage() {
      if (!this.managedAccounts) return '0%'
      return Math.round((this.activeAssignmentCount / this.managedAccounts) * 100) + '%'
    },
    trustedCoverage() {
      if (!this.managedAccounts) return '0%'
      return Math.round((this.accountsWithTrustedDevices / this.managedAccounts) * 100) + '%'
    },
    reviewCompletionRate() {
      if (!this.workflows.length) return '0%'
      return Math.round((this.approvedReviews / this.workflows.length) * 100) + '%'
    },
    displayPendingReviews() {
      return this.workflowsLoaded ? this.pendingReviews : '...'
    },
    displayTotalAgreements() {
      return this.workflowsLoaded ? this.totalAgreements : '...'
    },
    displayDraftAgreements() {
      return this.workflowsLoaded ? this.draftAgreements : '...'
    },
    displayTrustedDevices() {
      return this.accountsLoaded ? this.trustedDevices : '...'
    },
    summaryStats() {
      return [
        {
          label: this.text.totalAgreements,
          value: this.displayTotalAgreements,
          icon: 'A',
          iconClass: 'stat-icon--red',
          to: '/agreements'
        },
        {
          label: this.text.pendingReviews,
          value: this.displayPendingReviews,
          icon: 'R',
          iconClass: 'stat-icon--gold',
          to: '/review-queue'
        },
        {
          label: this.text.draftAgreements,
          value: this.displayDraftAgreements,
          icon: 'G',
          iconClass: 'stat-icon--green',
          to: '/agreements'
        },
        {
          label: this.text.trustedDevices,
          value: this.displayTrustedDevices,
          icon: 'T',
          iconClass: 'stat-icon--blue',
          to: '/reports'
        }
      ]
    },
    workflowChart() {
      const now = new Date()
      const buckets = Array.from({ length: 6 }, (_, index) => {
        const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1)
        return {
          key: date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0'),
          label: formatMonthLabel(date),
          draft: 0,
          pending: 0,
          approved: 0
        }
      })

      const lookup = buckets.reduce((map, item) => {
        map[item.key] = item
        return map
      }, {})

      this.workflows.forEach((item) => {
        const date = toDate(item.submittedAt || item.approvedAt)
        if (!date) return
        const key = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0')
        if (!lookup[key]) return
        if (item.status === 'approved') lookup[key].approved += 1
        else if (item.status === 'draft') lookup[key].draft += 1
        else lookup[key].pending += 1
      })

      return {
        labels: buckets.map(item => item.label),
        datasets: [
          {
            label: this.text.draft,
            backgroundColor: 'rgba(245, 158, 11, 0.82)',
            data: buckets.map(item => item.draft)
          },
          {
            label: this.text.pending,
            backgroundColor: 'rgba(140, 21, 21, 0.88)',
            data: buckets.map(item => item.pending)
          },
          {
            label: this.text.approved,
            backgroundColor: 'rgba(22, 163, 74, 0.82)',
            data: buckets.map(item => item.approved)
          }
        ]
      }
    },
    workflowChartBuckets() {
      const labels = this.workflowChart.labels || []
      const draft = this.workflowChart.datasets && this.workflowChart.datasets[0] ? this.workflowChart.datasets[0].data : []
      const pending = this.workflowChart.datasets && this.workflowChart.datasets[1] ? this.workflowChart.datasets[1].data : []
      const approved = this.workflowChart.datasets && this.workflowChart.datasets[2] ? this.workflowChart.datasets[2].data : []
      return labels.map((label, index) => ({
        label,
        draft: Number(draft[index] || 0),
        pending: Number(pending[index] || 0),
        approved: Number(approved[index] || 0),
        total: Number(draft[index] || 0) + Number(pending[index] || 0) + Number(approved[index] || 0)
      }))
    },
    chartBusiestMonth() {
      const top = this.workflowChartBuckets.reduce((best, item) => (!best || item.total > best.total ? item : best), null)
      if (!top || !top.total) return this.text.noActivityYet
      return `${top.label} (${top.total})`
    },
    chartTopStatus() {
      const totals = [
        { label: this.text.draft, value: this.draftAgreements },
        { label: this.text.pending, value: this.pendingReviews },
        { label: this.text.approved, value: this.approvedReviews }
      ]
      const top = totals.reduce((best, item) => (!best || item.value > best.value ? item : best), null)
      return top && top.value ? `${top.label} (${top.value})` : this.text.noDominantStatus
    },
    chartFocusMessage() {
      if (this.pendingReviews > this.approvedReviews && this.pendingReviews > this.draftAgreements) return this.text.focusClearPending
      if (this.draftAgreements > this.pendingReviews) return this.text.focusConvertDrafts
      if (this.approvedReviews) return this.text.focusSustainMomentum
      return this.text.focusBuildActivity
    },
    chartInsightHeadline() {
      if (!this.totalAgreements) return this.text.noAgreementActivity
      return this.text.recentWorkflowActivity(this.chartTopStatus)
    },
    agreementTypeRows() {
      const map = {}
      this.workflows.forEach((item) => {
        const key = item.type || this.text.agreementTypeFallback
        map[key] = (map[key] || 0) + 1
      })
      const items = Object.keys(map).map(key => ({ label: key, value: map[key] }))
      const rows = items.sort((left, right) => right.value - left.value).slice(0, 4)
      return rows.length ? rows : [{ label: this.text.noAgreementData, value: '-' }]
    },
    reviewStages() {
      const map = {}
      this.workflows.forEach((item) => {
        const key = getStageLabel(item)
        map[key] = (map[key] || 0) + 1
      })
      const items = Object.keys(map).map(key => ({ label: key, value: map[key] }))
      return items.sort((left, right) => right.value - left.value).slice(0, 4)
    },
    accountTrustRows() {
      return [
        { label: this.text.accountsWithTrustedDevices, value: this.accountsWithTrustedDevices },
        { label: this.text.accountsWithoutTrustedDevices, value: Math.max(this.managedAccounts - this.accountsWithTrustedDevices, 0) },
        { label: this.text.savedTrustedDevices, value: this.trustedDevices },
        { label: this.text.averageDevicesPerAccount, value: this.managedAccounts ? (this.trustedDevices / this.managedAccounts).toFixed(1) : '0.0' }
      ]
    },
    insightCards() {
      return [
        {
          title: this.text.agreementTypes,
          subtitle: this.text.agreementTypesCopy,
          items: this.agreementTypeRows
        },
        {
          title: this.text.stageDistribution,
          subtitle: this.text.stageDistributionCopy,
          items: this.reviewStages.length ? this.reviewStages : [{ label: this.text.noReviewData, value: '-' }]
        },
        {
          title: this.text.reviewSnapshot,
          subtitle: this.text.reviewSnapshotCopy,
          items: [
            { label: this.text.pendingReviewsStat, value: this.pendingReviews },
            { label: this.text.approvedAgreements, value: this.approvedReviews },
            { label: this.text.draftAgreementsStat, value: this.draftAgreements },
            { label: this.text.completionRate, value: this.reviewCompletionRate }
          ]
        }
      ]
    },
    queueItems() {
      return this.workflows
        .filter(item => item.status !== 'approved')
        .slice()
        .sort((left, right) => left.currentStep - right.currentStep)
        .slice(0, 4)
        .map((item) => ({
          id: item.id,
          title: item.title,
          meta: (item.type || this.text.agreementTypeFallback) + ' · ' + getStageLabel(item)
        }))
    },
    quickActions() {
      return [
        { label: this.text.quickActionViewAgreements, copy: this.text.quickActionViewAgreementsCopy, badge: this.totalAgreements, icon: 'A', to: '/agreements' },
        { label: this.text.quickActionOpenQueue, copy: this.text.quickActionOpenQueueCopy, badge: this.pendingReviews, icon: 'R', to: '/review-queue' },
        { label: this.text.quickActionTranslation, copy: this.text.quickActionTranslationCopy, badge: this.totalAgreements, icon: 'T', to: '/translation-requests' }
      ]
    }
  },
  created() {
    this.loadCachedDashboard()
    if (!isFreshTimestamp(this.lastUpdatedAt, ADMIN_DASHBOARD_CACHE_MAX_AGE)) {
      this.loadDashboard()
    }
  },
  methods: {
    applyDashboardData(payload = {}) {
      this.workflows = Array.isArray(payload.workflows) ? payload.workflows : []
      this.accounts = Array.isArray(payload.accounts) ? payload.accounts : []
      this.assignments = Array.isArray(payload.assignments) ? payload.assignments : []
      this.workflowsLoaded = !!payload.workflowsLoaded
      this.accountsLoaded = !!payload.accountsLoaded
      this.assignmentsLoaded = !!payload.assignmentsLoaded
    },
    loadCachedDashboard() {
      const cached = readPageCache(ADMIN_DASHBOARD_CACHE_KEY, ADMIN_DASHBOARD_CACHE_MAX_AGE)
      if (!cached || typeof cached !== 'object') return
      this.applyDashboardData(cached)
      this.lastUpdatedAt = cached.lastUpdatedAt || ''
    },
    persistDashboardCache() {
      writePageCache(ADMIN_DASHBOARD_CACHE_KEY, {
        workflows: this.workflows,
        accounts: this.accounts,
        assignments: this.assignments,
        workflowsLoaded: this.workflowsLoaded,
        accountsLoaded: this.accountsLoaded,
        assignmentsLoaded: this.assignmentsLoaded,
        lastUpdatedAt: this.lastUpdatedAt
      })
    },
    goTo(path) {
      if (path) this.$router.push(path)
    },
    openReview(id) {
      this.$router.push({
        path: '/review',
        query: { agreementId: id }
      })
    },
    exportDashboard() {
      this.$router.push('/reports')
    },
    refreshDashboard() {
      return this.loadDashboard({ forceRefresh: true })
    },
    formatRefreshTime(value) {
      if (!value) return this.text.justNow
      return new Date(value).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    mapAccount(item) {
      return {
        id: String(item && item._id ? item._id : ''),
        name: getAccountName(item),
        statusKey: item && item.status && item.status.key ? String(item.status.key) : 'UNKNOWN',
        trustedDevices: item && item.control && Array.isArray(item.control.trustedDevices)
          ? item.control.trustedDevices.length
          : 0
      }
    },
    mapAssignment(item) {
      return {
        id: String(item && item._id ? item._id : ''),
        active: !!(item && item.active),
        groupLabel: getGroupLabel(item && item.group)
      }
    },
    async loadDashboard(options = {}) {
      if (this.dashboardLoading) return
      this.dashboardLoading = true
      try {
        const [workflowRes, accountsRes, assignmentsRes] = await Promise.allSettled([
          Service.adminReview('queue', null, { forceRefresh: !!options.forceRefresh }),
          Service.accounts('list', null, { forceRefresh: !!options.forceRefresh }),
          Service.security('assignments', { active: true }, { forceRefresh: !!options.forceRefresh })
        ])

        if (workflowRes.status === 'fulfilled') {
          this.workflows = workflowRes.value && workflowRes.value.data && Array.isArray(workflowRes.value.data.data)
            ? workflowRes.value.data.data
            : []
          this.workflowsLoaded = true
        } else {
          this.workflows = []
          this.workflowsLoaded = false
        }

        if (accountsRes.status === 'fulfilled') {
          const records = accountsRes.value && accountsRes.value.data && Array.isArray(accountsRes.value.data.data)
            ? accountsRes.value.data.data
            : []
          this.accounts = records.map(this.mapAccount)
          this.accountsLoaded = true
        } else {
          this.accounts = []
          this.accountsLoaded = false
        }

        if (assignmentsRes.status === 'fulfilled') {
          const records = assignmentsRes.value && assignmentsRes.value.data && Array.isArray(assignmentsRes.value.data.data)
            ? assignmentsRes.value.data.data
            : []
          this.assignments = records.map(this.mapAssignment)
          this.assignmentsLoaded = true
        } else {
          this.assignments = []
          this.assignmentsLoaded = false
        }

        this.lastUpdatedAt = new Date().toISOString()
        this.persistDashboardCache()
      } finally {
        this.dashboardLoading = false
      }
    }
  }
}
</script>

<style scoped>
.dashboard-page-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  margin-bottom: 10px;
  padding: 0 2px;
}

.dashboard-page-actions__meta {
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
  padding: 9px 12px;
}

.dashboard-hero {
  background: linear-gradient(135deg, #8c1515 0%, #9e212f 42%, #c96b1b 100%) !important;
  border: 0 !important;
  border-radius: 1.25rem !important;
}

.hero-kicker {
  color: rgba(255, 248, 240, 0.82);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.hero-title {
  color: #fffaf5;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 0.85rem;
}

.hero-copy {
  color: rgba(255, 248, 240, 0.86);
  font-size: 1rem;
  margin-bottom: 1.25rem;
  max-width: 40rem;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
}

.hero-actions .btn-outline-secondary {
  border-color: rgba(255, 248, 240, 0.68);
  color: #fffaf5;
}

.hero-actions .btn-outline-secondary:hover,
.hero-actions .btn-outline-secondary:focus,
.hero-actions .btn-outline-secondary:active {
  background: rgba(255, 248, 240, 0.16);
  border-color: #fffaf5;
  color: #fffaf5;
}

.hero-side-card {
  background: rgba(255, 248, 240, 0.16) !important;
  border: 1px solid rgba(255, 248, 240, 0.3) !important;
  border-radius: 1rem !important;
  color: #fff;
  backdrop-filter: blur(6px);
}

.hero-side-label,
.stat-label {
  color: rgba(255, 248, 240, 0.88);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.hero-side-value {
  color: #fffaf5;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  margin-top: 0.5rem;
}

.hero-side-copy {
  color: rgba(255, 248, 240, 0.94);
  margin-top: 0.45rem;
}

.stat-card,
.panel-card {
  border-radius: 1rem;
}

.stat-card {
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.stat-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.stat-label {
  color: #7b8794;
}

.stat-value {
  color: #223045;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  margin-top: 0.5rem;
}

.stat-delta,
.panel-subtitle,
.quick-action__copy,
.queue-item__meta,
.data-row__label {
  color: #7b8794;
}

.stat-icon {
  align-items: center;
  border-radius: 0.8rem;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 700;
  height: 2.3rem;
  justify-content: center;
  width: 2.3rem;
}

.stat-icon--red {
  background: #fee8e8;
  color: #9e212f;
}

.stat-icon--gold {
  background: #fff4df;
  color: #b56a00;
}

.stat-icon--green {
  background: #e7faf3;
  color: #1d7a46;
}

.stat-icon--blue {
  background: #f8edd9;
  color: #9e212f;
}

.data-row__value,
.queue-item__title,
.quick-action__label {
  color: #344767;
  font-weight: 700;
}

.panel-header {
  padding: 1.35rem 1.5rem 0.75rem;
}

.panel-title {
  color: #344767;
  font-size: 1.25rem;
  font-weight: 700;
}

.panel-title--small {
  font-size: 1rem;
}

.chart-card-body {
  min-height: 210px;
}

.panel-card--chart {
  height: auto;
}

.chart-insights {
  border-top: 1px solid #eef1f6;
  margin-top: 1rem;
  padding-top: 1rem;
}

.chart-insights__headline {
  color: #52606d;
  font-size: 0.95rem;
  line-height: 1.5;
}

.chart-insights__grid {
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 1rem;
}

.chart-insights__item {
  background: #f8fafc;
  border: 1px solid #e8eef5;
  border-radius: 0.9rem;
  padding: 0.85rem 0.95rem;
}

.chart-insights__label {
  color: #7b8794;
  display: block;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.chart-insights__value {
  color: #344767;
  display: block;
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.4;
  margin-top: 0.35rem;
}

.quick-action {
  align-items: center;
  background: transparent;
  border: 0;
  border-top: 1px solid #eef1f6;
  cursor: pointer;
  display: grid;
  gap: 0.8rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 1rem 0;
  text-align: left;
  width: 100%;
}

.quick-action__icon {
  align-items: center;
  background: #fff4df;
  border-radius: 0.8rem;
  color: #8c1515;
  display: inline-flex;
  font-size: 0.9rem;
  font-weight: 700;
  height: 2.3rem;
  justify-content: center;
  width: 2.3rem;
}

.quick-action__content {
  display: flex;
  flex-direction: column;
}

.quick-action__badge {
  background: #f8fafc;
  border-radius: 999px;
  color: #4f5d75;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.3rem 0.65rem;
}

.data-list {
  display: flex;
  flex-direction: column;
}

.data-row,
.queue-item {
  align-items: center;
  border-top: 1px solid #eef1f6;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: 0.95rem 0;
}

.queue-item__content {
  min-width: 0;
}

.admin-dashboard {
  padding-bottom: 1rem;
}

@media (max-width: 767.98px) {
  .hero-title {
    font-size: 1.7rem;
  }

  .chart-insights__grid {
    grid-template-columns: 1fr;
  }

  .quick-action,
  .data-row,
  .queue-item {
    grid-template-columns: 1fr;
  }
}
</style>
